// The buyer form — ONE component serving both `/buyers/new` and
// `/buyers/:id/edit`, branching on the route param. Two components would be two
// copies of a thirty-field form, and the copy that drifts is the one nobody is
// looking at (the BrokerEditPage shape).

import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import {
  buyersApi,
  type BuyerClearFlagKey,
  type BuyerLeadDetailDto,
  type BuyerLeadSource,
  type CreateBuyerLeadPayload,
  type UpdateBuyerLeadPayload,
} from "@/api/buyers";
import {
  AdvertisementPicker,
  useAdvertisementAttribution,
} from "@/components/buyers/AdvertisementPicker";
import {
  SpecialistCandidateSelect,
  SpecialistQuickPick,
} from "@/components/buyers/SpecialistQuickPick";
import { BuyerTypeCreateFields } from "@/components/buyers/BuyerTypeCreateFields";
import {
  EMPTY_BUYER_TYPE_CREATE,
  buyerTypeCreatePayload,
  companyDetailsProblem,
  type BuyerTypeCreateState,
} from "@/components/buyers/buyerTypeCreate";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import { Button, FormField, Input, useToast } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { extractErrorMessage } from "@/api/client";
import { emailFieldError } from "@/lib/emailValidator";
import { phoneFieldError } from "@/lib/phoneValidator";
import { US_STATES } from "@/lib/usStates";
import { BUYER_SOURCES, buyerLabel } from "./buyerFilters";
import { assignmentNotice } from "./QuickAddBuyerModal";

/** Money and counts are held as STRINGS so a half-typed figure survives a
 *  keystroke; they are parsed once, on save. */
interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  zip: string;

  // §2.2 / D2: Source and Source detail only. The campaign code and the nine
  // UTM/ad fields no longer exist — the ADVERTISEMENT is the attribution.
  source: BuyerLeadSource;
  sourceDetail: string;

  liquidCapital: string;
  netWorth: string;
  maxPurchasePrice: string;
  downPaymentAvailable: string;
  creditScoreBand: string;

  targetIndustries: string;
  targetGeographies: string;
  targetRevenueMin: string;
  targetRevenueMax: string;
  targetCashFlowMin: string;
  timelineToPurchase: string;

  industryExperience: string;
  managementExperience: string;
  /** "" is "unknown", which is NOT the same answer as "no". */
  isSbaCandidate: "" | "yes" | "no";
  financingNotes: string;
  relocationWillingness: string;

  notes: string;
}

const EMPTY: FormState = {
  firstName: "", lastName: "", email: "", phone: "", city: "", state: "", zip: "",
  source: "Unknown", sourceDetail: "",
  liquidCapital: "", netWorth: "", maxPurchasePrice: "", downPaymentAvailable: "",
  creditScoreBand: "",
  targetIndustries: "", targetGeographies: "", targetRevenueMin: "",
  targetRevenueMax: "", targetCashFlowMin: "", timelineToPurchase: "",
  industryExperience: "", managementExperience: "", isSbaCandidate: "",
  financingNotes: "", relocationWillingness: "",
  notes: "",
};

const str = (v: string | null | undefined): string => v ?? "";
const num = (v: number | null | undefined): string => (v == null ? "" : String(v));

/** Empty or unparseable is `null` — never `0`, which is a real answer. */
function toNumber(v: string): number | null {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t.replace(/[$,]/g, ""));
  return Number.isFinite(n) ? n : null;
}

function hydrate(b: BuyerLeadDetailDto): FormState {
  return {
    firstName: str(b.firstName),
    lastName: str(b.lastName),
    email: str(b.email),
    phone: str(b.phone),
    city: str(b.city),
    state: str(b.state),
    zip: str(b.zip),
    source: b.source,
    sourceDetail: str(b.sourceDetail),
    liquidCapital: num(b.liquidCapital),
    netWorth: num(b.netWorth),
    maxPurchasePrice: num(b.maxPurchasePrice),
    downPaymentAvailable: num(b.downPaymentAvailable),
    creditScoreBand: num(b.creditScoreBand),
    targetIndustries: str(b.targetIndustries),
    targetGeographies: str(b.targetGeographies),
    targetRevenueMin: num(b.targetRevenueMin),
    targetRevenueMax: num(b.targetRevenueMax),
    targetCashFlowMin: num(b.targetCashFlowMin),
    timelineToPurchase: str(b.timelineToPurchase),
    industryExperience: str(b.industryExperience),
    managementExperience: str(b.managementExperience),
    isSbaCandidate: b.isSbaCandidate == null ? "" : b.isSbaCandidate ? "yes" : "no",
    financingNotes: str(b.financingNotes),
    relocationWillingness: str(b.relocationWillingness),
    notes: str(b.notes),
  };
}

/** The fields both modes send — the patch shape minus its clear flags, which is
 *  exactly what the create payload extends. */
type BuyerFieldSet = Omit<UpdateBuyerLeadPayload, BuyerClearFlagKey>;

/**
 * The shared field set.
 *
 * <p>Strings are sent as typed: `""` CLEARS server-side, and this form hydrates
 * every field and always sends all of them, so an emptied box means the
 * specialist emptied it (the FIND-12 convention this module documents on
 * `UpdateBuyerLeadRequest`).</p>
 *
 * <p>No `campaign` and no UTM key: those fields were retired (D2) and are not
 * on the wire at all.</p>
 *
 * <p>No buyer-type or acquiring-company key either, and that is deliberate:
 * this set is sent whole on EDIT, where an always-sent `""` would clear what the
 * record's Contact section owns. "Who is buying?" is asked on create only and
 * its keys are added on the create branch alone (`buyerTypeCreatePayload`).</p>
 */
function toPayload(f: FormState): BuyerFieldSet {
  return {
    firstName: f.firstName.trim(),
    lastName: f.lastName.trim(),
    email: f.email.trim(),
    phone: f.phone.trim(),
    city: f.city.trim(),
    state: f.state,
    zip: f.zip.trim(),

    source: f.source,
    sourceDetail: f.sourceDetail.trim(),

    liquidCapital: toNumber(f.liquidCapital),
    netWorth: toNumber(f.netWorth),
    maxPurchasePrice: toNumber(f.maxPurchasePrice),
    downPaymentAvailable: toNumber(f.downPaymentAvailable),
    creditScoreBand: toNumber(f.creditScoreBand),

    targetIndustries: f.targetIndustries.trim(),
    targetGeographies: f.targetGeographies.trim(),
    targetRevenueMin: toNumber(f.targetRevenueMin),
    targetRevenueMax: toNumber(f.targetRevenueMax),
    targetCashFlowMin: toNumber(f.targetCashFlowMin),
    timelineToPurchase: f.timelineToPurchase.trim(),

    industryExperience: f.industryExperience.trim(),
    managementExperience: f.managementExperience.trim(),
    isSbaCandidate: f.isSbaCandidate === "" ? null : f.isSbaCandidate === "yes",
    financingNotes: f.financingNotes.trim(),
    relocationWillingness: f.relocationWillingness.trim(),

    notes: f.notes.trim(),
  };
}

export function BuyerEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const editing = !!id;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof FormState>(key: K, val: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: val }));

  // Create-only: an advertisement is picked at intake. Re-attributing a stored
  // lead is a separate decision this form does not take.
  const attribution = useAdvertisementAttribution((s) => set("source", s));
  // Create-only too (Q3): null = the queue. The server writes the same
  // grant-ledger row for an assignee named here that the queue's assign path
  // writes. On an existing buyer the owner is moved through
  // `SpecialistQuickPick` below, which posts to the assignment endpoints —
  // never through this form's patch, which does not accept it.
  const [assignTo, setAssignTo] = useState<string | null>(null);
  // Create-only as well: "Who is buying?". Editing these on an existing buyer
  // belongs to the record's Contact section, never to this form's patch.
  const [buyerType, setBuyerType] = useState<BuyerTypeCreateState>(EMPTY_BUYER_TYPE_CREATE);
  // The company rows' required errors show once a save has been attempted.
  const [showCompanyErrors, setShowCompanyErrors] = useState(false);

  const existing = useQuery({
    queryKey: buyerKeys.detail(id ?? ""),
    queryFn: () => buyersApi.get(id!),
    enabled: editing,
  });

  // The URL-derived default renders the raw GUID; show the buyer's name instead.
  useBreadcrumbs(
    editing
      ? existing.data
        ? [
            { label: "Buyers", href: "/buyers" },
            { label: existing.data.displayName, href: `/buyers/${id}` },
            { label: "Edit" },
          ]
        : null
      : [
          { label: "Buyers", href: "/buyers" },
          { label: "New buyer" },
        ],
  );

  // Hydrate ONCE per record, not on every new identity of `existing.data`.
  // The edit form now mounts an inline reassign control whose write
  // invalidates `buyerKeys.all`; the refetch that follows hands back a new
  // object, and an effect keyed on it alone would re-hydrate the form and
  // overwrite everything the specialist had typed since opening it (the
  // "prefill re-runs the hydration effect" trap). Keyed on the record's id, so
  // navigating from one buyer's edit page to another's still hydrates.
  const hydratedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!existing.data || hydratedFor.current === existing.data.id) return;
    hydratedFor.current = existing.data.id;
    setForm(hydrate(existing.data));
  }, [existing.data]);

  const save = useMutation({
    mutationFn: () => {
      if (!editing) {
        const body: CreateBuyerLeadPayload = {
          ...toPayload(form),
          // Blank type adds no key at all; see `buyerTypeCreatePayload`.
          ...buyerTypeCreatePayload(buyerType),
          // Omitted rather than sent null: no pick is no attribution, and the
          // picker never renders for a caller without BuyerAdvertising.Read.
          ...(attribution.advertisementId
            ? { advertisementId: attribution.advertisementId }
            : {}),
          // Likewise: omitted lands the buyer unassigned in the queue.
          ...(assignTo ? { assignToSpecialistUserId: assignTo } : {}),
        };
        return buyersApi.create(body);
      }
      const body: UpdateBuyerLeadPayload = toPayload(form);
      return buyersApi.update(id!, body);
    },
    onSuccess: (buyer) => {
      void queryClient.invalidateQueries({ queryKey: buyerKeys.all });
      if (!editing) {
        // Read off the response — the server decided the row's team (Q4 B).
        const notice = assignmentNotice(buyer);
        if (notice) toast.info(notice);
      }
      navigate(`/buyers/${buyer.id}`);
    },
    onError: (e: unknown) => setError(extractErrorMessage(e)),
  });

  // Optional, but the server rejects a malformed one — catch it here rather
  // than letting a 400 land after a page of typing.
  const emailError = emailFieldError(form.email, { required: false });
  const phoneError = phoneFieldError(form.phone, { required: false });
  const hasName = !!form.firstName.trim() || !!form.lastName.trim();

  // Server-resolved ownership (L35). Rendering a form that is guaranteed to 403
  // is worse than saying so up front; the server enforces it either way.
  const readOnly = editing && existing.data ? !existing.data.canEdit : false;
  const canSave = hasName && !emailError && !phoneError && !readOnly;

  return (
    <>
      <PageHeader
        eyebrow="Buyer CRM"
        title={editing ? "Edit buyer" : "New buyer"}
        actions={
          <>
            <Button size="sm" variant="secondary" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button
              size="sm"
              loading={save.isPending}
              disabled={!canSave}
              onClick={() => {
                setError(null);
                if (!hasName) {
                  setError("Enter at least a first or last name.");
                  return;
                }
                if (emailError || phoneError) {
                  setError(emailError ?? phoneError ?? null);
                  return;
                }
                // The server refuses the same thing with the same words; saying
                // it here keeps the specialist on the field that needs it.
                const companyProblem = editing
                  ? null
                  : companyDetailsProblem(buyerType.buyerType, buyerType.companyName, buyerType.title);
                if (companyProblem) {
                  setShowCompanyErrors(true);
                  setError(companyProblem);
                  return;
                }
                save.mutate();
              }}
            >
              <Save size={14} strokeWidth={1.5} aria-hidden /> Save buyer
            </Button>
          </>
        }
      />

      {readOnly && (
        <div
          className="rounded-md border border-status-info bg-white-warm px-space-3 py-space-2 text-small text-onyx mb-space-3"
          role="status"
        >
          This buyer belongs to another consultant, so it is read-only for you.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField label="First name">
          <Input value={form.firstName} onChange={(e) => set("firstName", e.target.value)} />
        </FormField>
        <FormField label="Last name">
          <Input value={form.lastName} onChange={(e) => set("lastName", e.target.value)} />
        </FormField>
        <FormField label="Email" error={emailError}>
          <Input
            type="email"
            value={form.email}
            invalid={!!emailError}
            onChange={(e) => set("email", e.target.value)}
          />
        </FormField>
        <FormField label="Phone" error={phoneError}>
          <Input
            type="tel"
            value={form.phone}
            invalid={!!phoneError}
            onChange={(e) => set("phone", e.target.value)}
          />
        </FormField>
        <FormField label="City">
          <Input value={form.city} onChange={(e) => set("city", e.target.value)} />
        </FormField>
        <FormField label="State">
          <Select value={form.state} onChange={(e) => set("state", e.target.value)}>
            <option value="">—</option>
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
            ))}
          </Select>
        </FormField>
        <FormField label="ZIP">
          <Input value={form.zip} onChange={(e) => set("zip", e.target.value)} />
        </FormField>
        {/* Create-only — see `buyerType` above. */}
        {!editing && (
          <BuyerTypeCreateFields
            value={buyerType}
            onChange={setBuyerType}
            showErrors={showCompanyErrors}
          />
        )}
      </div>

      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mt-space-4 mb-space-2">
        Ownership
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        {editing ? (
          // ONE component decides whether this is a control or plain text:
          // `canReassign` is the server's answer (BuyerReassignmentRule), and
          // a confirmed move posts to the assignment endpoints with a reason —
          // it saves on its own, not with the form. Wrapped in a `<div>` on
          // purpose: FormField mints an id only for a labelable child, so the
          // label reads as a heading here instead of pointing at nothing.
          <FormField
            label="Consultant"
            helperText="Moving a buyer records a reason and saves immediately, separately from this form."
          >
            <div>
              <SpecialistQuickPick
                buyerLeadId={id!}
                currentSpecialistUserId={existing.data?.assignedSpecialistUserId}
                currentSpecialistName={existing.data?.assignedSpecialistName}
                canReassign={existing.data?.canReassign ?? false}
                canUnassign={existing.data?.canUnassign ?? false}
                buyerDisplayName={existing.data?.displayName}
              />
            </div>
          </FormField>
        ) : (
          <FormField
            label="Assign to"
            helperText="Leave unassigned and the buyer lands in the queue."
          >
            <SpecialistCandidateSelect buyerLeadId={null} value={assignTo} onChange={setAssignTo} />
          </FormField>
        )}
      </div>

      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mt-space-4 mb-space-2">
        Attribution
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField
          label="Source"
          helperText={
            editing
              ? undefined
              : "Picking an advertisement fills this in; change it and your choice stays."
          }
        >
          <Select
            value={form.source}
            onChange={(e) => attribution.setSourceByHand(e.target.value as BuyerLeadSource)}
          >
            {BUYER_SOURCES.map((s) => (
              <option key={s} value={s}>{buyerLabel(s)}</option>
            ))}
          </Select>
        </FormField>
        <FormField label="Source detail" helperText="Free text — “referred by Dave at Acme”.">
          <Input value={form.sourceDetail} onChange={(e) => set("sourceDetail", e.target.value)} />
        </FormField>
        {/* The advertisement IS the attribution (§2.2, D2). Create-only — see
            `attribution` above. */}
        {!editing && (
          <AdvertisementPicker
            value={attribution.advertisementId}
            onChange={attribution.pickAdvertisement}
          />
        )}
      </div>

      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mt-space-4 mb-space-2">
        Capacity
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField label="Liquid capital">
          <Input value={form.liquidCapital} onChange={(e) => set("liquidCapital", e.target.value)} />
        </FormField>
        <FormField label="Net worth">
          <Input value={form.netWorth} onChange={(e) => set("netWorth", e.target.value)} />
        </FormField>
        <FormField label="Max purchase price">
          <Input value={form.maxPurchasePrice} onChange={(e) => set("maxPurchasePrice", e.target.value)} />
        </FormField>
        <FormField label="Down payment available">
          <Input value={form.downPaymentAvailable} onChange={(e) => set("downPaymentAvailable", e.target.value)} />
        </FormField>
        <FormField label="Credit score band">
          <Input
            type="number"
            value={form.creditScoreBand}
            onChange={(e) => set("creditScoreBand", e.target.value)}
          />
        </FormField>
      </div>

      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mt-space-4 mb-space-2">
        What they&apos;re looking for
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField label="Target industries" className="md:col-span-2">
          <Input value={form.targetIndustries} onChange={(e) => set("targetIndustries", e.target.value)} />
        </FormField>
        <FormField label="Target geographies" className="md:col-span-2">
          <Input value={form.targetGeographies} onChange={(e) => set("targetGeographies", e.target.value)} />
        </FormField>
        <FormField label="Target revenue min">
          <Input value={form.targetRevenueMin} onChange={(e) => set("targetRevenueMin", e.target.value)} />
        </FormField>
        <FormField label="Target revenue max">
          <Input value={form.targetRevenueMax} onChange={(e) => set("targetRevenueMax", e.target.value)} />
        </FormField>
        <FormField label="Target cash flow min">
          <Input value={form.targetCashFlowMin} onChange={(e) => set("targetCashFlowMin", e.target.value)} />
        </FormField>
        <FormField label="Timeline to purchase">
          <Input value={form.timelineToPurchase} onChange={(e) => set("timelineToPurchase", e.target.value)} />
        </FormField>
      </div>

      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mt-space-4 mb-space-2">
        Experience &amp; financing
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField label="Industry experience" className="md:col-span-2">
          <Textarea rows={3} value={form.industryExperience} onChange={(e) => set("industryExperience", e.target.value)} />
        </FormField>
        <FormField label="Management experience" className="md:col-span-2">
          <Textarea rows={3} value={form.managementExperience} onChange={(e) => set("managementExperience", e.target.value)} />
        </FormField>
        <FormField label="SBA candidate">
          <Select
            value={form.isSbaCandidate}
            onChange={(e) => set("isSbaCandidate", e.target.value as FormState["isSbaCandidate"])}
          >
            <option value="">Unknown</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </Select>
        </FormField>
        <FormField label="Relocation willingness">
          <Input value={form.relocationWillingness} onChange={(e) => set("relocationWillingness", e.target.value)} />
        </FormField>
        <FormField label="Financing notes" className="md:col-span-2">
          <Textarea rows={3} value={form.financingNotes} onChange={(e) => set("financingNotes", e.target.value)} />
        </FormField>
      </div>

      {/* Follow-ups are NOT a field here any more: they are WorkTasks,
          scheduled from the buyer's record or the list row's "Schedule
          follow-up" action, so a date on this form would be a second place the
          same fact is written. */}
      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mt-space-4 mb-space-2">
        Notes
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField label="Notes" className="md:col-span-2">
          <Textarea rows={4} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </FormField>
      </div>

      {/* Status is deliberately NOT here: it moves through the milestone-
          stamping path, so a bare field on a contact edit would let it drift.
          Ownership is above, and never a bare field either — on create the
          server writes the grant ledger for the named assignee; on edit the
          move goes through the assignment endpoints, not this patch. */}

      {error && (
        <p className="text-caption text-status-error mt-space-3" role="alert">
          {error}
        </p>
      )}
    </>
  );
}
